import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import ServiceCard from '../components/ui/ServiceCard';
import { containerVariants, itemVariants } from '../utils/animations';
import { services, ctaSection } from '../data/content';
import { Link } from 'react-router-dom';

const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<number | null>(null);

  return (
    <>
      <Helmet>
        <title>Our Services - SOFTCONCERN TECHNOLOGIES</title>
        <meta name="description" content="Comprehensive IT services including AI automation, custom software development, cloud solutions, and IT consulting." />
        <meta name="keywords" content="AI automation, software development, cloud solutions, IT consulting, digital transformation" />
        <meta property="og:title" content="Our Services - SOFTCONCERN TECHNOLOGIES" />
        <meta property="og:description" content="Comprehensive IT services including AI automation, custom software development, cloud solutions, and IT consulting." />
        <link rel="canonical" href="/services" />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              variants={itemVariants}
            >
              Our IT <span className="text-primary-600">Services</span>
            </motion.h1>
            <motion.p 
              className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto"
              variants={itemVariants}
            >
              SOFTCONCERN TECHNOLOGIES LLP offers a comprehensive range of IT services 
              designed to empower your business in the digital age.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div key={service.id} variants={itemVariants}>
                <ServiceCard
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  keyOfferings={service.keyOfferings}
                  onClick={() => setSelectedService(service.id)}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Detailed Service Sections */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.category.toLowerCase().replace(/\s+/g, '-')}
                className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium mb-4">
                      {service.category}
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Offerings:</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.keyOfferings.map((offering, offeringIndex) => (
                          <li key={offeringIndex} className="flex items-center space-x-2">
                            <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />
                            <span className="text-gray-700">{offering}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to="/contact"
                      className="btn-primary inline-flex items-center space-x-2"
                    >
                      <span>Get Started</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                  <div className="relative">
                    <div className="bg-gradient-to-br from-primary-100 to-purple-100 rounded-2xl p-8 text-center">
                      <div className="text-6xl mb-4">
                        {service.icon === 'Brain' ? '🤖' : 
                         service.icon === 'Code' ? '💻' :
                         service.icon === 'Cloud' ? '☁️' :
                         service.icon === 'Users' ? '👥' : '⚡'}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {service.category}
                      </h3>
                      <p className="text-gray-600">
                        Professional {service.category.toLowerCase()} services tailored to your business needs.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Service Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We follow a proven methodology to ensure successful project delivery and client satisfaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Consultation',
                description: 'Understanding your business requirements and challenges'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'Developing a comprehensive solution strategy'
              },
              {
                step: '03',
                title: 'Implementation',
                description: 'Executing the solution with precision and quality'
              },
              {
                step: '04',
                title: 'Support',
                description: 'Ongoing support and maintenance for optimal performance'
              }
            ].map((process, index) => (
              <motion.div
                key={process.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {process.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-primary-50">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose SOFTCONCERN?
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We combine technical expertise with business understanding to deliver 
                solutions that drive real results for your organization.
              </p>
              
              <div className="space-y-4">
                {[
                  'Experienced team of IT professionals',
                  'Proven track record of successful projects',
                  'Cutting-edge technology solutions',
                  'Competitive pricing and transparent billing',
                  '24/7 customer support and maintenance',
                  'Agile development methodology'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-primary-600" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">🏆</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Award-Winning Services
                  </h3>
                  <p className="text-gray-600">
                    Recognized for excellence in IT solutions and customer satisfaction
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">100%</div>
                    <div className="text-sm text-gray-600">Client Satisfaction</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">5+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">50+</div>
                    <div className="text-sm text-gray-600">Projects Delivered</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600 mb-1">24/7</div>
                    <div className="text-sm text-gray-600">Support</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="container">
          <motion.div
            className="text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {ctaSection.title}
            </h2>
            <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
              Let's discuss how our services can help transform your business and drive growth.
            </p>
            <Link
              to={ctaSection.buttonHref}
              className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <span>{ctaSection.buttonText}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;